import React, { useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { tasksAtom } from '../../../../atoms/v0/taskAtoms';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';

/**
 * 专注页面进度统计组件
 * 显示今日任务完成情况和趋势图表
 */
export const FocusProgressStats: React.FC = () => {
  const { t } = useTranslation(['v3/focus', 'common']);
  const tasks = useAtomValue(tasksAtom);
  const isDark = useAtomValue(isDarkAtom);

  // 切换视图状态 - 默认显示日趋势
  const [activeView, setActiveView] = useState<'daily' | 'weekly' | 'monthly' | 'productivity'>('daily');

  // 累积模式开关 - 默认开启，三个趋势图表共享此状态
  const [isCumulativeMode, setIsCumulativeMode] = useState(true);

  // 根据主题获取颜色
  const getThemeColors = () => {
    if (isDark) {
      return {
        gridStroke: '#3A3A3C',
        tickFill: '#8E8E93',
        tooltipBg: '#2C2C2E',
        tooltipText: '#FFFFFF'
      };
    } else {
      return {
        gridStroke: '#E5E5EA',
        tickFill: '#AEAEB2',
        tooltipBg: '#FFFFFF',
        tooltipText: '#000000'
      };
    }
  };

  const themeColors = getThemeColors();

  // 计算今天的日期范围（从今天0点到23:59:59）
  const todayRange = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return {
      start: today,
      end: tomorrow
    };
  }, []);

  // 计算过去7天的日期范围
  const pastWeekRange = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 6);
    weekAgo.setHours(0, 0, 0, 0);

    return {
      start: weekAgo,
      end: today
    };
  }, []);

  // 计算过去30天的日期范围
  const pastMonthRange = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    const monthAgo = new Date(today);
    monthAgo.setDate(monthAgo.getDate() - 29);
    monthAgo.setHours(0, 0, 0, 0);

    return {
      start: monthAgo,
      end: today
    };
  }, []);

  // 计算今日任务统计
  const todayStats = useMemo(() => {
    // 所有任务数量
    const allTasks = tasks.filter(task => {
      // 如果任务有开始时间或截止时间，检查是否在今天
      if (task.startAt || task.dueAt) {
        const taskDate = new Date(task.startAt || task.dueAt || '');
        return taskDate >= todayRange.start && taskDate < todayRange.end;
      }
      return false;
    });

    // 已完成的任务
    const completedTasks = allTasks.filter(task => task.done);

    // 进行中的任务（已开始但未完成）
    const inProgressTasks = allTasks.filter(task =>
      !task.done && task.startAt && new Date(task.startAt) <= new Date()
    );

    // 未开始的任务
    const notStartedTasks = allTasks.filter(task =>
      !task.done && (!task.startAt || new Date(task.startAt) > new Date())
    );

    return {
      total: allTasks.length,
      completed: completedTasks.length,
      inProgress: inProgressTasks.length,
      notStarted: notStartedTasks.length,
      completionRate: allTasks.length > 0 ? (completedTasks.length / allTasks.length) * 100 : 0
    };
  }, [tasks, todayRange]);

  // 计算过去7天每天的完成情况
  const weeklyCompletionData = useMemo(() => {
    const days: { date: Date; name: string; completed: number; due: number; started: number }[] = [];

    // 创建过去7天的日期数组
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);

      // 格式化日期为短格式（如 "5/12"）
      const name = `${date.getMonth() + 1}/${date.getDate()}`;

      days.push({
        date,
        name,
        completed: 0,
        due: 0,
        started: 0
      });
    }

    // 统计每天的任务完成情况
    tasks.forEach(task => {
      // 检查任务是否有完成时间
      if (task.done && task.doneAt) {
        const doneDate = new Date(task.doneAt);

        // 检查完成时间是否在过去7天内
        if (doneDate >= pastWeekRange.start && doneDate <= pastWeekRange.end) {
          // 找到对应的日期
          const dayIndex = days.findIndex(day =>
            doneDate.getDate() === day.date.getDate() &&
            doneDate.getMonth() === day.date.getMonth() &&
            doneDate.getFullYear() === day.date.getFullYear()
          );

          if (dayIndex !== -1) {
            days[dayIndex].completed++;
          }
        }
      }

      // 检查任务是否在过去7天内计划（dueAt）
      if (task.dueAt) {
        const dueDate = new Date(task.dueAt);

        if (dueDate >= pastWeekRange.start && dueDate <= pastWeekRange.end) {
          // 找到对应的日期
          const dayIndex = days.findIndex(day =>
            dueDate.getDate() === day.date.getDate() &&
            dueDate.getMonth() === day.date.getMonth() &&
            dueDate.getFullYear() === day.date.getFullYear()
          );

          if (dayIndex !== -1) {
            days[dayIndex].due++;
          }
        }
      }

      // 检查任务是否在过去7天内开始（startAt）
      if (task.startAt) {
        const startDate = new Date(task.startAt);

        if (startDate >= pastWeekRange.start && startDate <= pastWeekRange.end) {
          // 找到对应的日期
          const dayIndex = days.findIndex(day =>
            startDate.getDate() === day.date.getDate() &&
            startDate.getMonth() === day.date.getMonth() &&
            startDate.getFullYear() === day.date.getFullYear()
          );

          if (dayIndex !== -1) {
            days[dayIndex].started++;
          }
        }
      }
    });

    // 计算累积值
    let cumulativeDue = 0;
    let cumulativeStarted = 0;
    let cumulativeCompleted = 0;

    return days.map(dayData => {
      cumulativeDue += dayData.due;
      cumulativeStarted += dayData.started;
      cumulativeCompleted += dayData.completed;

      return {
        ...dayData,
        cumulativeDue,
        cumulativeStarted,
        cumulativeCompleted
      };
    });
  }, [tasks, pastWeekRange]);

  // 计算过去30天每天的完成情况
  const monthlyCompletionData = useMemo(() => {
    const days: { date: Date; name: string; completed: number; due: number; started: number }[] = [];

    // 创建过去30天的日期数组
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);

      // 格式化日期为短格式（如 "5/12"）
      const name = `${date.getMonth() + 1}/${date.getDate()}`;

      days.push({
        date,
        name,
        completed: 0,
        due: 0,
        started: 0
      });
    }

    // 统计每天的任务情况
    tasks.forEach(task => {
      // 检查任务是否在过去30天内完成（doneAt）
      if (task.done && task.doneAt) {
        const doneDate = new Date(task.doneAt);

        if (doneDate >= pastMonthRange.start && doneDate <= pastMonthRange.end) {
          // 找到对应的日期
          const dayIndex = days.findIndex(day =>
            doneDate.getDate() === day.date.getDate() &&
            doneDate.getMonth() === day.date.getMonth() &&
            doneDate.getFullYear() === day.date.getFullYear()
          );

          if (dayIndex !== -1) {
            days[dayIndex].completed++;
          }
        }
      }

      // 检查任务是否在过去30天内计划（dueAt）
      if (task.dueAt) {
        const dueDate = new Date(task.dueAt);

        if (dueDate >= pastMonthRange.start && dueDate <= pastMonthRange.end) {
          // 找到对应的日期
          const dayIndex = days.findIndex(day =>
            dueDate.getDate() === day.date.getDate() &&
            dueDate.getMonth() === day.date.getMonth() &&
            dueDate.getFullYear() === day.date.getFullYear()
          );

          if (dayIndex !== -1) {
            days[dayIndex].due++;
          }
        }
      }

      // 检查任务是否在过去30天内开始（startAt）
      if (task.startAt) {
        const startDate = new Date(task.startAt);

        if (startDate >= pastMonthRange.start && startDate <= pastMonthRange.end) {
          // 找到对应的日期
          const dayIndex = days.findIndex(day =>
            startDate.getDate() === day.date.getDate() &&
            startDate.getMonth() === day.date.getMonth() &&
            startDate.getFullYear() === day.date.getFullYear()
          );

          if (dayIndex !== -1) {
            days[dayIndex].started++;
          }
        }
      }
    });

    // 计算累积值
    let cumulativeDue = 0;
    let cumulativeStarted = 0;
    let cumulativeCompleted = 0;

    return days.map(dayData => {
      cumulativeDue += dayData.due;
      cumulativeStarted += dayData.started;
      cumulativeCompleted += dayData.completed;

      return {
        ...dayData,
        cumulativeDue,
        cumulativeStarted,
        cumulativeCompleted
      };
    });
  }, [tasks, pastMonthRange]);

  // 计算每小时的任务完成情况（生产力热图）
  const hourlyCompletionData = useMemo(() => {
    // 初始化24小时的数组
    const hours = Array(24).fill(0).map((_, index) => ({
      hour: index,
      count: 0
    }));

    // 统计过去30天内每个小时完成的任务数量
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    thirtyDaysAgo.setHours(0, 0, 0, 0);

    tasks.forEach(task => {
      if (task.done && task.doneAt) {
        const doneDate = new Date(task.doneAt);

        // 只统计过去30天内完成的任务
        if (doneDate >= thirtyDaysAgo) {
          const hour = doneDate.getHours();
          hours[hour].count++;
        }
      }
    });

    // 计算最大值，用于归一化
    const maxCount = Math.max(...hours.map(h => h.count), 1);

    // 添加强度属性（0-1之间的值）
    return hours.map(h => ({
      ...h,
      intensity: h.count / maxCount
    }));
  }, [tasks]);

  // 计算今日任务进度数据（按小时）
  const dailyProgressData = useMemo(() => {
    // 初始化24小时的数组 - 使用24小时制
    const hours = Array(24).fill(0).map((_, index) => ({
      hour: index,
      name: index < 10 ? `0${index}:00` : `${index}:00`,
      started: 0,
      completed: 0
    }));

    // 只统计今天的任务
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    tasks.forEach(task => {
      // 检查任务是否今天开始
      if (task.startAt) {
        const startDate = new Date(task.startAt);
        if (startDate >= today && startDate < tomorrow) {
          const hour = startDate.getHours();
          hours[hour].started++;
        }
      }

      // 检查任务是否今天完成
      if (task.done && task.doneAt) {
        const doneDate = new Date(task.doneAt);
        if (doneDate >= today && doneDate < tomorrow) {
          const hour = doneDate.getHours();
          hours[hour].completed++;
        }
      }
    });

    // 计算累计值（模拟Linear的曲线图）
    let cumulativeStarted = 0;
    let cumulativeCompleted = 0;

    return hours.map(hourData => {
      cumulativeStarted += hourData.started;
      cumulativeCompleted += hourData.completed;

      return {
        ...hourData,
        cumulativeStarted,
        cumulativeCompleted
      };
    });
  }, [tasks]);

  // 计算周图表的最大值，确保有足够的空间
  const weeklyChartMaxValue = useMemo(() => {
    if (isCumulativeMode) {
      const maxCumulativeDue = Math.max(...weeklyCompletionData.map(day => day.cumulativeDue));
      const maxCumulativeStarted = Math.max(...weeklyCompletionData.map(day => day.cumulativeStarted));
      const maxCumulativeCompleted = Math.max(...weeklyCompletionData.map(day => day.cumulativeCompleted));
      return Math.max(maxCumulativeDue, maxCumulativeStarted, maxCumulativeCompleted, 5);
    } else {
      const maxDue = Math.max(...weeklyCompletionData.map(day => day.due));
      const maxStarted = Math.max(...weeklyCompletionData.map(day => day.started));
      const maxCompleted = Math.max(...weeklyCompletionData.map(day => day.completed));
      return Math.max(maxDue, maxStarted, maxCompleted, 5);
    }
  }, [weeklyCompletionData, isCumulativeMode]);

  // 计算月图表的最大值，确保有足够的空间
  const monthlyChartMaxValue = useMemo(() => {
    if (isCumulativeMode) {
      const maxCumulativeDue = Math.max(...monthlyCompletionData.map(day => day.cumulativeDue));
      const maxCumulativeStarted = Math.max(...monthlyCompletionData.map(day => day.cumulativeStarted));
      const maxCumulativeCompleted = Math.max(...monthlyCompletionData.map(day => day.cumulativeCompleted));
      return Math.max(maxCumulativeDue, maxCumulativeStarted, maxCumulativeCompleted, 5);
    } else {
      const maxDue = Math.max(...monthlyCompletionData.map(day => day.due));
      const maxStarted = Math.max(...monthlyCompletionData.map(day => day.started));
      const maxCompleted = Math.max(...monthlyCompletionData.map(day => day.completed));
      return Math.max(maxDue, maxStarted, maxCompleted, 5);
    }
  }, [monthlyCompletionData, isCumulativeMode]);

  // 计算日进度图表的最大值
  const dailyChartMaxValue = useMemo(() => {
    if (dailyProgressData.length === 0) return 5;
    if (isCumulativeMode) {
      const lastHourData = dailyProgressData[dailyProgressData.length - 1];
      return Math.max(lastHourData.cumulativeStarted, lastHourData.cumulativeCompleted, 5);
    } else {
      const maxStarted = Math.max(...dailyProgressData.map(hour => hour.started));
      const maxCompleted = Math.max(...dailyProgressData.map(hour => hour.completed));
      return Math.max(maxStarted, maxCompleted, 5);
    }
  }, [dailyProgressData, isCumulativeMode]);

  return (
    <div className="bg-white dark:bg-ios-dark-gray5 rounded-ios-lg overflow-hidden shadow-ios dark:shadow-ios-dark mb-6">
      <div className="p-4">
        <h3 className="text-ios-title3 font-semibold text-ios-black dark:text-white mb-3">
          {t('v3/focus:stats.todayProgress')}
        </h3>

        {/* 进度统计卡片 - 重新排序：进行中 → 已完成 → 总数量 */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-ios-yellow/10 dark:bg-ios-dark-yellow/10 rounded-ios-md p-3">
            <div className="text-xs text-ios-gray dark:text-ios-dark-gray mb-1">{t('v3/focus:stats.inProgress')}</div>
            <div className="text-2xl font-semibold text-ios-yellow dark:text-ios-dark-yellow">{todayStats.inProgress}</div>
          </div>

          <div className="bg-ios-blue/10 dark:bg-ios-blue/10 rounded-ios-md p-3">
            <div className="text-xs text-ios-gray dark:text-ios-dark-gray mb-1">{t('v3/focus:stats.completed')}</div>
            <div className="text-2xl font-semibold text-ios-blue dark:text-ios-blue">{todayStats.completed}</div>
          </div>

          <div className="bg-ios-gray6/50 dark:bg-ios-dark-gray6/50 rounded-ios-md p-3">
            <div className="text-xs text-ios-gray dark:text-ios-dark-gray mb-1">{t('v3/focus:stats.total')}</div>
            <div className="text-2xl font-semibold text-ios-black dark:text-white">{todayStats.total}</div>
          </div>
        </div>

        {/* 完成率进度条 */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-1">
            <div className="text-xs text-ios-gray dark:text-ios-dark-gray">{t('v3/focus:stats.completionRate')}</div>
            <div className="text-xs font-medium text-ios-black dark:text-white">
              {todayStats.completionRate.toFixed(0)}%
            </div>
          </div>
          <div className="h-2 bg-ios-gray6 dark:bg-ios-dark-gray6 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-ios-blue dark:bg-ios-blue rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${todayStats.completionRate}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            ></motion.div>
          </div>
        </div>

        {/* 视图切换器 - 重新排序：日趋势 → 周趋势 → 月趋势 → 生产力 */}
        <div className="flex justify-center mb-4">
          <div className="bg-ios-gray6/50 dark:bg-ios-dark-gray6/50 rounded-full p-1 inline-flex">
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                activeView === 'daily'
                  ? 'bg-white dark:bg-ios-dark-gray5 text-ios-black dark:text-white shadow-sm'
                  : 'text-ios-gray dark:text-ios-dark-gray'
              }`}
              onClick={() => setActiveView('daily')}
            >
              {t('v3/focus:stats.dailyTrend')}
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                activeView === 'weekly'
                  ? 'bg-white dark:bg-ios-dark-gray5 text-ios-black dark:text-white shadow-sm'
                  : 'text-ios-gray dark:text-ios-dark-gray'
              }`}
              onClick={() => setActiveView('weekly')}
            >
              {t('v3/focus:stats.weeklyTrend')}
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                activeView === 'monthly'
                  ? 'bg-white dark:bg-ios-dark-gray5 text-ios-black dark:text-white shadow-sm'
                  : 'text-ios-gray dark:text-ios-dark-gray'
              }`}
              onClick={() => setActiveView('monthly')}
            >
              {t('v3/focus:stats.monthlyTrend')}
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                activeView === 'productivity'
                  ? 'bg-white dark:bg-ios-dark-gray5 text-ios-black dark:text-white shadow-sm'
                  : 'text-ios-gray dark:text-ios-dark-gray'
              }`}
              onClick={() => setActiveView('productivity')}
            >
              {t('v3/focus:stats.productivity')}
            </button>
          </div>
        </div>

        {/* 累积模式开关 - 只在趋势图表时显示 */}
        {(activeView === 'daily' || activeView === 'weekly' || activeView === 'monthly') && (
          <div className="flex justify-center mb-4">
            <div className="flex items-center space-x-2">
              <span className="text-xs text-ios-gray dark:text-ios-dark-gray">
                {t('v3/focus:stats.cumulativeMode')}
              </span>
              <button
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isCumulativeMode
                    ? 'bg-ios-blue dark:bg-ios-dark-blue'
                    : 'bg-ios-gray5 dark:bg-ios-dark-gray5'
                }`}
                onClick={() => setIsCumulativeMode(!isCumulativeMode)}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isCumulativeMode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        )}

        {/* 周趋势图表 */}
        {activeView === 'weekly' && (
          <div className="h-64">
            <div className="text-xs text-ios-gray dark:text-ios-dark-gray mb-2 text-center">
              {t('v3/focus:stats.weeklyTasksDescription')}
            </div>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={weeklyCompletionData}
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorWeeklyPlanned" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FFCC00" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#FFCC00" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorWeeklyStarted" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FF9500" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#FF9500" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorWeeklyCompleted" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#007AFF" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#007AFF" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke={themeColors.gridStroke}
                  opacity={0.3}
                />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 0.625, fill: themeColors.tickFill }}
                />
                <YAxis
                  hide={true}
                  domain={[0, weeklyChartMaxValue]}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: themeColors.tooltipBg,
                    border: 'none',
                    borderRadius: '0.5rem',
                    color: themeColors.tooltipText,
                    fontSize: '0.75rem',
                    padding: '0.5rem',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                  }}
                  itemStyle={{ color: 'white' }}
                  labelStyle={{ color: 'white', fontWeight: 'bold', marginBottom: '0.25rem' }}
                  formatter={(value, name) => {
                    const displayName =
                      name === 'due' ? t('v3/focus:stats.due') :
                      name === 'started' ? t('v3/focus:stats.started') :
                      name === 'completed' ? t('v3/focus:stats.completed') :
                      name === 'cumulativeDue' ? t('v3/focus:stats.cumulativeDue') :
                      name === 'cumulativeStarted' ? t('v3/focus:stats.cumulativeStarted') :
                      name === 'cumulativeCompleted' ? t('v3/focus:stats.cumulativeCompleted') : name;
                    return [value, displayName];
                  }}
                  labelFormatter={(label) => `${label}`}
                />
                <Area
                  type="monotone"
                  dataKey={isCumulativeMode ? "cumulativeDue" : "due"}
                  stroke="#FFCC00"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorWeeklyPlanned)"
                  name={isCumulativeMode ? t('v3/focus:stats.cumulativeDue') : t('v3/focus:stats.due')}
                  activeDot={{ r: 0.375, fill: "#FFCC00", stroke: "white", strokeWidth: 0.125 }}
                />
                <Area
                  type="monotone"
                  dataKey={isCumulativeMode ? "cumulativeStarted" : "started"}
                  stroke="#FF9500"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorWeeklyStarted)"
                  name={isCumulativeMode ? t('v3/focus:stats.cumulativeStarted') : t('v3/focus:stats.started')}
                  activeDot={{ r: 0.375, fill: "#FF9500", stroke: "white", strokeWidth: 0.125 }}
                />
                <Area
                  type="monotone"
                  dataKey={isCumulativeMode ? "cumulativeCompleted" : "completed"}
                  stroke="#007AFF"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorWeeklyCompleted)"
                  name={isCumulativeMode ? t('v3/focus:stats.cumulativeCompleted') : t('v3/focus:stats.completed')}
                  activeDot={{ r: 0.375, fill: "#007AFF", stroke: "white", strokeWidth: 0.125 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* 日趋势图表 */}
        {activeView === 'daily' && (
          <div className="h-64">
            <div className="text-xs text-ios-gray dark:text-ios-dark-gray mb-2 text-center">
              {t('v3/focus:stats.dailyTasksDescription')}
            </div>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={dailyProgressData.filter((_, index) => index % 2 === 0)} // 每2小时取一个点，减少密度
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorStarted" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FFCC00" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#FFCC00" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorCompleted" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#007AFF" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#007AFF" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke={themeColors.gridStroke}
                  opacity={0.3}
                />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 0.625, fill: themeColors.tickFill }}
                  interval={1}
                />
                <YAxis
                  hide={true}
                  domain={[0, dailyChartMaxValue]}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: themeColors.tooltipBg,
                    border: 'none',
                    borderRadius: '0.5rem',
                    color: themeColors.tooltipText,
                    fontSize: '0.75rem',
                    padding: '0.5rem',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                  }}
                  itemStyle={{ color: 'white' }}
                  labelStyle={{ color: 'white', fontWeight: 'bold', marginBottom: '0.25rem' }}
                  formatter={(value, name) => {
                    const displayName =
                      name === 'started' ? t('v3/focus:stats.started') :
                      name === 'completed' ? t('v3/focus:stats.completed') :
                      name === 'cumulativeStarted' ? t('v3/focus:stats.cumulativeStarted') :
                      name === 'cumulativeCompleted' ? t('v3/focus:stats.cumulativeCompleted') : name;
                    return [value, displayName];
                  }}
                  labelFormatter={(label) => `${label}`}
                />
                <Area
                  type="monotone"
                  dataKey={isCumulativeMode ? "cumulativeStarted" : "started"}
                  stroke="#FFCC00"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorStarted)"
                  name={isCumulativeMode ? t('v3/focus:stats.cumulativeStarted') : t('v3/focus:stats.started')}
                  activeDot={{ r: 0.375, fill: "#FFCC00", stroke: "white", strokeWidth: 0.125 }}
                />
                <Area
                  type="monotone"
                  dataKey={isCumulativeMode ? "cumulativeCompleted" : "completed"}
                  stroke="#007AFF"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorCompleted)"
                  name={isCumulativeMode ? t('v3/focus:stats.cumulativeCompleted') : t('v3/focus:stats.completed')}
                  activeDot={{ r: 0.375, fill: "#007AFF", stroke: "white", strokeWidth: 0.125 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* 月趋势图表 */}
        {activeView === 'monthly' && (
          <div className="h-64">
            <div className="text-xs text-ios-gray dark:text-ios-dark-gray mb-2 text-center">
              {t('v3/focus:stats.monthlyTasksDescription')}
            </div>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={monthlyCompletionData.filter((_, index) => index % 2 === 0)} // 每2天取一个点，减少密度
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorMonthlyPlanned" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FFCC00" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#FFCC00" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorMonthlyStarted" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FF9500" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#FF9500" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorMonthlyCompleted" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#007AFF" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#007AFF" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke={themeColors.gridStroke}
                  opacity={0.3}
                />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 0.625, fill: themeColors.tickFill }}
                  interval={1}
                />
                <YAxis
                  hide={true}
                  domain={[0, monthlyChartMaxValue]}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: themeColors.tooltipBg,
                    border: 'none',
                    borderRadius: '0.5rem',
                    color: themeColors.tooltipText,
                    fontSize: '0.75rem',
                    padding: '0.5rem',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                  }}
                  itemStyle={{ color: 'white' }}
                  labelStyle={{ color: 'white', fontWeight: 'bold', marginBottom: '0.25rem' }}
                  formatter={(value, name) => {
                    const displayName =
                      name === 'due' ? t('v3/focus:stats.due') :
                      name === 'started' ? t('v3/focus:stats.started') :
                      name === 'completed' ? t('v3/focus:stats.completed') :
                      name === 'cumulativeDue' ? t('v3/focus:stats.cumulativeDue') :
                      name === 'cumulativeStarted' ? t('v3/focus:stats.cumulativeStarted') :
                      name === 'cumulativeCompleted' ? t('v3/focus:stats.cumulativeCompleted') : name;
                    return [value, displayName];
                  }}
                  labelFormatter={(label) => `${label}`}
                />
                <Area
                  type="monotone"
                  dataKey={isCumulativeMode ? "cumulativeDue" : "due"}
                  stroke="#FFCC00"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorMonthlyPlanned)"
                  name={isCumulativeMode ? t('v3/focus:stats.cumulativeDue') : t('v3/focus:stats.due')}
                  activeDot={{ r: 0.375, fill: "#FFCC00", stroke: "white", strokeWidth: 0.125 }}
                />
                <Area
                  type="monotone"
                  dataKey={isCumulativeMode ? "cumulativeStarted" : "started"}
                  stroke="#FF9500"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorMonthlyStarted)"
                  name={isCumulativeMode ? t('v3/focus:stats.cumulativeStarted') : t('v3/focus:stats.started')}
                  activeDot={{ r: 0.375, fill: "#FF9500", stroke: "white", strokeWidth: 0.125 }}
                />
                <Area
                  type="monotone"
                  dataKey={isCumulativeMode ? "cumulativeCompleted" : "completed"}
                  stroke="#007AFF"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorMonthlyCompleted)"
                  name={isCumulativeMode ? t('v3/focus:stats.cumulativeCompleted') : t('v3/focus:stats.completed')}
                  activeDot={{ r: 0.375, fill: "#007AFF", stroke: "white", strokeWidth: 0.125 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* 生产力热图 */}
        {activeView === 'productivity' && (
          <div>
            <div className="text-xs text-ios-gray dark:text-ios-dark-gray mb-2 text-center">
              {t('v3/focus:stats.productivityDescription')}
            </div>

            <div className="flex flex-wrap justify-center">
              {hourlyCompletionData.map((hourData) => {
                // 计算颜色强度（从淡蓝到深蓝）
                const intensity = hourData.intensity;
                // 使用更好的颜色渐变，从浅蓝到深蓝
                const color = intensity === 0
                  ? 'rgba(0, 122, 255, 0.1)'
                  : `rgba(${Math.round(0 + (0 * (1 - intensity)))}, ${Math.round(122 + (30 * (1 - intensity)))}, ${Math.round(255 - (55 * (1 - intensity)))}, ${0.2 + (intensity * 0.8)})`;

                // 格式化小时显示 - 使用24小时制
                const hourDisplay = hourData.hour < 10 ? `0${hourData.hour}:00` : `${hourData.hour}:00`;

                return (
                  <div key={`hour-${hourData.hour}`} className="m-1 relative group">
                    <motion.div
                      className="w-8 h-8 rounded-md flex items-center justify-center text-white text-xs font-medium cursor-pointer shadow-sm"
                      style={{ backgroundColor: color }}
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.3, delay: hourData.hour * 0.03 }}
                      whileHover={{ scale: 1.1, boxShadow: '0 0.25rem 0.375rem rgba(0, 0, 0, 0.1)' }}
                    >
                      {hourData.count > 0 ? hourData.count : ''}
                    </motion.div>

                    {/* 悬停提示 */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-ios-gray8 dark:bg-ios-dark-gray8 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                      {hourDisplay}: {hourData.count} {t('v3/focus:stats.tasks')}
                    </div>

                    {/* 小时标签 - 只显示部分时间点 */}
                    {(hourData.hour % 3 === 0 || hourData.hour === 23) && (
                      <div className="text-[0.625rem] text-ios-gray2 dark:text-ios-dark-gray2 text-center mt-1">
                        {hourDisplay}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-center items-center mt-4">
              <div className="w-full max-w-xs flex items-center">
                <div className="text-[0.625rem] text-ios-gray2 dark:text-ios-dark-gray2 mr-2">{t('v3/focus:stats.less')}</div>
                <div className="flex-1 h-2 rounded-full bg-gradient-to-r from-blue-100 via-blue-400 to-blue-600 shadow-inner"></div>
                <div className="text-[0.625rem] text-ios-gray2 dark:text-ios-dark-gray2 ml-2">{t('v3/focus:stats.more')}</div>
              </div>
            </div>

            <div className="mt-4 text-xs text-center text-ios-gray dark:text-ios-dark-gray">
              <p>{t('v3/focus:stats.productiveTimeSlots')}: {(() => {
                // 找出最高效的时段（完成任务最多的3小时区间）
                const sortedHours = [...hourlyCompletionData].sort((a, b) => b.count - a.count);
                const topHours = sortedHours.slice(0, 3).filter(h => h.count > 0);

                if (topHours.length === 0) return t('v3/focus:stats.noData');

                return topHours.map(h => {
                  const hourDisplay = h.hour < 10 ? `0${h.hour}:00` : `${h.hour}:00`;
                  return hourDisplay;
                }).join('、');
              })()}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
